import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import {
  getCurrentUser,
  signInWithRedirect,
  signIn,
  signOut,
  fetchUserAttributes,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword
} from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import './App.css';
import awsConfig from './aws-exports';
import { getApiEndpoint } from './lib/apiEndpoint';
import { postRequest } from './tools/httpRequest';
import AdminPanel from './components/AdminPanel.jsx';
import InvitationAccept from './components/InvitationAccept.jsx';
import ClinicSwitcher from './components/ClinicSwitcher.jsx';
import PatientRequestForm from './components/PatientRequestForm.jsx';
import QuotaDisplay from './components/QuotaDisplay.jsx';
import QuotaBanner from './components/QuotaBanner.jsx';
import { permissionManager } from './lib/permissions';
import { quotaManager } from './lib/quotaManager';
import { clinicManager } from './lib/clinicManager';
import { topicManager } from './lib/topicManager';
import { REPORT_TYPES, SCROLL_THRESHOLD, IFRAME_IDS, iframeIdManager } from './utils/constants';
import { setReportInIframe, onReportContentLoad } from './utils/iframeHelpers';
import { useIframeScrollRestoration } from './hooks/useIframeScrollRestoration';
import Header from './components/layout/Header.jsx';
import {
  UserIcon,
  SettingsIcon,
  SignOutIcon,
  ClinicIcon,
  EyeIcon,
  EyeOffIcon,
  WarningIcon,
  ChartIcon
} from './components/icons/Icons.jsx';

// Removed unused MultiTenancy context to avoid confusion

// Styles as objects for reuse
const styles = {
  container: {
    textAlign: 'center',
    height: '100%',
    width: '100%',
  }
};

// Initialize AWS configuration
const configureAmplify = () => {
  const origin = window.location.origin + "/";
  const updatedAwsConfig = {
    ...awsConfig,
    oauth: {
      ...awsConfig.oauth,
      redirectSignIn: origin,
      redirectSignOut: origin,
      responseType: "code",
      scope: ['phone', 'email', 'openid', 'profile', 'aws.cognito.signin.user.admin']
    }
  };
  // Configure dynamic API endpoint based on environment
  const dynamicApiEndpoint = getApiEndpoint();

  // Add or update API configuration
  if (!updatedAwsConfig.aws_cloud_logic_custom) {
    updatedAwsConfig.aws_cloud_logic_custom = [];
  }

  // Check if DailyReportAPI already exists
  const existingApiIndex = updatedAwsConfig.aws_cloud_logic_custom.findIndex(
    api => api?.name === 'DailyReportAPI'
  );

  if (existingApiIndex >= 0) {
    // Update existing API endpoint
    updatedAwsConfig.aws_cloud_logic_custom[existingApiIndex].endpoint = dynamicApiEndpoint;
  } else {
    // Add new API configuration
    updatedAwsConfig.aws_cloud_logic_custom.push({
      name: 'DailyReportAPI',
      endpoint: dynamicApiEndpoint,
      region: updatedAwsConfig.aws_project_region
    });
  }
  Amplify.configure(updatedAwsConfig);
};

configureAmplify();

// Password Strength Indicator Component
const PasswordStrengthIndicator = ({ password }) => {
  const getStrength = () => {
    if (!password) return { score: 0, label: '', color: '#e0e0e0' };

    let score = 0;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 2) return { score, label: 'Weak', color: '#dc3545' };
    if (score === 3) return { score, label: 'Fair', color: '#ffc107' };
    if (score === 4) return { score, label: 'Good', color: '#28a745' };
    return { score, label: 'Strong', color: '#0d6efd' };
  };

  const strength = getStrength();

  if (!password) return null;

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{
        height: '4px',
        backgroundColor: '#e0e0e0',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${(strength.score / 5) * 100}%`,
          backgroundColor: strength.color,
          transition: 'all 0.3s ease'
        }} />
      </div>
      <p style={{
        fontSize: '12px',
        color: strength.color,
        margin: '4px 0 0 0',
        fontWeight: '500'
      }}>
        {strength.label}
      </p>
    </div>
  );
};

// Sign In Component with Dual Authentication
const SignInSection = ({ onSignInSuccess }) => {
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetStep, setResetStep] = useState('email'); // 'email', 'code', 'success'
  const [showPassword, setShowPassword] = useState(false);

  const handleEmailPasswordSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn({ username: email, password });
      // Manually trigger user fetch after successful sign-in
      if (onSignInSuccess) {
        onSignInSuccess();
      }
    } catch (err) {
      console.error('Sign in error:', err);
      if (err.name === 'PasswordResetRequiredException') {
        setError('Your password has expired. Please reset it to continue.');
        setResetEmail(email);
        setShowPasswordReset(true);
      } else if (err.name === 'NotAuthorizedException') {
        setError('Incorrect email or password. Please try again.');
      } else if (err.name === 'UserNotFoundException') {
        setError('No account found with this email. Please check your email or sign up.');
      } else {
        setError(err.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowPasswordReset(true);
    setResetEmail(email);
    setError('');
  };

  const handleResetPasswordRequest = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await resetPassword({ username: resetEmail });
      setResetStep('code');
    } catch (err) {
      console.error('Password reset request error:', err);
      setError(err.message || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters long.');
      return;
    }

    setLoading(true);

    try {
      await confirmResetPassword({
        username: resetEmail,
        confirmationCode: resetCode,
        newPassword
      });

      // Auto-login after successful password reset
      await signIn({ username: resetEmail, password: newPassword });

      // Note: Password change notification
      // In production, you would call a backend API here to send the notification email
      // For now, this is documented in amplify/backend/function/PASSWORD_CHANGE_NOTIFICATION_SETUP.md

      setResetStep('success');
    } catch (err) {
      console.error('Password reset confirmation error:', err);
      if (err.name === 'CodeMismatchException') {
        setError('Invalid or expired code. Please try again or request a new code.');
      } else if (err.name === 'InvalidPasswordException') {
        setError('Password does not meet requirements. Use uppercase, lowercase, numbers, and symbols.');
      } else {
        setError(err.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    setShowPasswordReset(false);
    setResetStep('email');
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  // Password Reset Flow
  if (showPasswordReset) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '40px 20px',
        textAlign: 'center'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '40px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxWidth: '400px',
          width: '100%'
        }}>
          <h2 style={{
            color: '#333',
            fontSize: '24px',
            marginBottom: '20px',
            fontWeight: '600'
          }}>
            {resetStep === 'email' ? 'Reset Password' : resetStep === 'code' ? 'Enter Reset Code' : 'Password Reset Complete'}
          </h2>

          {error && (
            <div style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {resetStep === 'email' && (
            <form onSubmit={handleResetPasswordRequest} style={{ width: '100%' }}>
              <input
                type="email"
                placeholder="Email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '20px',
                textAlign: 'left'
              }}>
                We'll send a 6-digit code to your email. The code expires in 7 days.
              </p>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  marginBottom: '12px'
                }}
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
              <button
                type="button"
                onClick={handleBackToSignIn}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  backgroundColor: 'transparent',
                  color: '#007bff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Back to Sign In
              </button>
            </form>
          )}

          {resetStep === 'code' && (
            <form onSubmit={handleConfirmReset} style={{ width: '100%' }}>
              <input
                type="text"
                placeholder="6-digit code"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                required
                maxLength="6"
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  boxSizing: 'border-box',
                  textAlign: 'center',
                  letterSpacing: '4px'
                }}
              />
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  boxSizing: 'border-box'
                }}
              />
              <PasswordStrengthIndicator password={newPassword} />
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  marginTop: '12px',
                  marginBottom: '16px',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{
                fontSize: '12px',
                color: '#666',
                marginBottom: '20px',
                textAlign: 'left',
                lineHeight: '1.5'
              }}>
                Password must be at least 12 characters with uppercase, lowercase, numbers, and symbols.
                <br />
                <strong>Tip:</strong> Use a password manager to create and store secure passwords.
              </p>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  marginBottom: '12px'
                }}
              >
                {loading ? 'Resetting...' : 'Reset Password & Sign In'}
              </button>
              <button
                type="button"
                onClick={handleBackToSignIn}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  backgroundColor: 'transparent',
                  color: '#007bff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Main Sign In Form
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      padding: '40px 20px',
      textAlign: 'center'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        maxWidth: '400px',
        width: '100%'
      }}>
        <h2 style={{
          color: '#333',
          fontSize: '24px',
          marginBottom: '20px',
          fontWeight: '600'
        }}>
          Sign In to Care Management Portal
        </h2>

        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleEmailPasswordSignIn} style={{ width: '100%', marginBottom: '20px' }}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              marginBottom: '12px',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: '40px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginBottom: '12px',
                boxSizing: 'border-box'
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
              style={{
                position: 'absolute',
                right: '12px',
                top: '12px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#666'
              }}
            >
              {showPassword ? <EyeIcon /> : <EyeOffIcon />}
            </button>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '16px'
          }}>
            <button
              type="button"
              onClick={handleForgotPassword}
              style={{
                background: 'none',
                border: 'none',
                color: '#007bff',
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Forgot Password?
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '16px',
              fontWeight: '500',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          margin: '20px 0',
          color: '#999',
          fontSize: '14px'
        }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }} />
          <span style={{ padding: '0 12px' }}>OR</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }} />
        </div>

        <button
          onClick={() => signInWithRedirect({ provider: 'Google' })}
          className="google-sign-in-button"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            padding: '12px 20px',
            fontSize: '16px',
            fontWeight: '500',
            backgroundColor: 'white',
            border: '2px solid #4285f4',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            marginBottom: '20px'
          }}
        >
          <img
            src="https://developers.google.com/identity/images/g-logo.png"
            alt="Google logo"
            style={{ width: '20px', height: '20px', marginRight: '10px' }}
          />
          Sign in with Google
        </button>
        <div style={{
          color: '#666',
          fontSize: '14px',
          lineHeight: '1.5'
        }}>
          <p style={{ margin: '0' }}>Need access to a clinic?</p>
          <p style={{ margin: '0' }}>Contact your administrator for an invitation.</p>
        </div>
      </div>
    </div>
  );
};

// Enhanced Clinic Switcher Component - now imported from separate file
// const ClinicSwitcher = ({ currentUser, currentClinic, availableClinics, onClinicSwitch }) => {
/*if (!currentUser || !availableClinics || availableClinics.length <= 1) {
  return null;
}

const switcherStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 8px',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderRadius: '6px',
  fontSize: '0.85rem'
};

const selectStyles = {
  border: 'none',
  background: 'transparent',
  fontSize: '0.85rem',
  fontWeight: 'bold',
  color: '#333',
  cursor: 'pointer'
};

return (
  <div style={switcherStyles}>
    <ClinicIcon style={{ marginRight: '6px' }} />
    <select
      value={currentClinic?.id || ''}
      onChange={(e) => onClinicSwitch(e.target.value)}
      style={selectStyles}
      title="Switch Clinic"
    >
      {availableClinics.map(clinic => (
        <option key={clinic.id} value={clinic.id}>
          {clinic.name}
        </option>
      ))}
    </select>
  </div>
);
// }; */

// User Info Component with Dropdown
const UserInfo = ({ currentUser, onSignOut, onAdminClick, clinics = [] }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!currentUser) return null;

  const canViewAdminPanel = permissionManager.canViewAdminPanel(currentUser);

  const userIconStyles = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#4285f4',
      color: 'white'
    }
  };

  const dropdownStyles = {
    position: 'absolute',
    top: '40px',
    right: '0',
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    minWidth: '200px',
    zIndex: 1000,
    overflow: 'hidden'
  };

  const dropdownItemStyles = {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem',
    transition: 'background-color 0.2s ease'
  };

  const dropdownItemHoverStyles = {
    backgroundColor: '#f8f9fa'
  };

  const userNameStyles = {
    fontWeight: 'bold',
    color: '#333'
  };

  const userEmailStyles = {
    color: '#666',
    fontSize: '0.8rem'
  };

  const clinicsListStyles = {
    padding: '8px 16px',
    borderBottom: '1px solid #f0f0f0',
    backgroundColor: '#fff'
  };

  const clinicItemStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '0.85rem',
    color: '#444',
    padding: '4px 0'
  };

  const clinicRoleBadge = (role) => ({
    marginLeft: '8px',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.7rem',
    backgroundColor: role === 'super_admin' ? '#fdecea' : role === 'admin' ? '#fff3cd' : '#e2f0e9',
    color: role === 'super_admin' ? '#b71c1c' : role === 'admin' ? '#856404' : '#0f5132',
    border: '1px solid ' + (role === 'super_admin' ? '#f5c6cb' : role === 'admin' ? '#ffeeba' : '#badbcc')
  });

  const handleAdminClick = () => {
    setIsDropdownOpen(false);
    onAdminClick();
  };

  const handleSignOutClick = () => {
    setIsDropdownOpen(false);
    onSignOut();
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <div
        style={{
          ...userIconStyles,
          backgroundColor: isHovered ? '#4285f4' : 'rgba(255, 255, 255, 0.9)'
        }}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        title="User Menu"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <UserIcon color={isHovered ? 'white' : '#333'} />
      </div>

      {isDropdownOpen && (
        <div style={dropdownStyles}>
          {/* User Info Section */}
          <div style={{ ...dropdownItemStyles, borderBottom: '2px solid #e9ecef', cursor: 'default' }}>
            <div>
              <div style={userNameStyles}>
                {currentUser.firstName || currentUser.email}
              </div>
              {currentUser.firstName && (
                <div style={userEmailStyles}>
                  {currentUser.email}
                </div>
              )}
            </div>
          </div>

          {/* Clinics list for this user */}
          {Array.isArray(clinics) && clinics.length > 0 && (
            <div style={clinicsListStyles}>
              <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: 6 }}>Clinics</div>
              <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                {clinics.map(c => (
                  <div key={c.id} style={clinicItemStyles}>
                    <span style={{ fontWeight: 600 }}>{c.name || c.id}</span>
                    <span style={clinicRoleBadge(c.role)}>{c.role}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* System Management */}
          {canViewAdminPanel && (
            <div
              style={dropdownItemStyles}
              onClick={handleAdminClick}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
            >
              <SettingsIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Management
            </div>
          )}

          {/* Sign Out */}
          <div
            style={{ ...dropdownItemStyles, borderBottom: 'none', color: '#dc3545' }}
            onClick={handleSignOutClick}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
          >
            <SignOutIcon style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Sign Out
          </div>
        </div>
      )}
    </div>
  );
};

// Button Bar Component
const ButtonBar = ({ selectedReport, handleReportSelection, reportAvailability, showShadow, currentUser, onAdminClick, onClinicSwitch, onSignOut, isLoading, error, userTopics = [], availableClinics = [], selectedClinics = [], onClinicSelectionChange, onHeightChange }) => {
  const hasAnyTopics = userTopics.length > 0;
  const hasAnyClinics = availableClinics.length > 0;
  const showClinicControls = !!currentUser?.isSuperAdmin; // Only super admins see clinic controls
  const headerRef = useRef(null);

  // Clinic quota state
  const [clinicQuotas, setClinicQuotas] = useState({});
  const [quotasLoading, setQuotasLoading] = useState(false);

  // Report header height changes
  useEffect(() => {
    if (!headerRef.current || !onHeightChange) return;
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        onHeightChange(entry.contentRect.height);
      }
    });
    resizeObserver.observe(headerRef.current);
    // Initial measurement
    onHeightChange(headerRef.current.offsetHeight);
    return () => resizeObserver.disconnect();
  }, [onHeightChange]);

  // Load quotas for all clinics with quota definitions
  useEffect(() => {
    const loadClinicQuotas = async () => {
      if (!availableClinics.length) return;

      setQuotasLoading(true);
      const quotas = {};

      try {
        await Promise.all(
          availableClinics.map(async (clinic) => {
            try {
              const status = await quotaManager.getClinicQuotaStatus(clinic.id);
              // Only include clinics with quota definitions (limit can be 0)
              if (status && status.quotaStatus !== 'no_quota' && status.quota?.limit !== undefined && status.quota?.limit !== null) {
                quotas[clinic.id] = {
                  name: clinic.name,
                  patientMonths: status.patientMonths || 0,
                  limit: status.quota.limit,
                  quotaStatus: status.quotaStatus
                };
              }
            } catch (err) {
              console.error(`Error loading quota for clinic ${clinic.id}:`, err);
            }
          })
        );
        setClinicQuotas(quotas);
      } catch (err) {
        console.error('Error loading clinic quotas:', err);
      } finally {
        setQuotasLoading(false);
      }
    };

    loadClinicQuotas();
  }, [availableClinics]);

  // Get quota pill color based on status
  const getQuotaPillColors = (status) => {
    switch (status) {
      case 'ok':
        return { bg: '#e8f5e9', text: '#2e7d32', dot: '#4caf50' };
      case 'warning':
        return { bg: '#fff3e0', text: '#e65100', dot: '#ff9800' };
      case 'exceeded':
        return { bg: '#ffebee', text: '#c62828', dot: '#f44336' };
      default:
        return { bg: '#f5f5f5', text: '#616161', dot: '#9e9e9e' };
    }
  };

  // Render quota banner using QuotaBanner component
  const QuotaPills = () => {
    const clinicIds = Object.keys(clinicQuotas);
    if (clinicIds.length === 0) return null;

    // Find which clinic owns the selected topic
    const getClinicForTopic = (topicId) => {
      if (!topicId) return null;
      return availableClinics.find(clinic =>
        clinic.topics && clinic.topics.includes(topicId)
      );
    };

    // Calculate quota status based on actual values
    const getQuotaStatus = (patientMonths, limit) => {
      if (limit === null || limit === undefined) return 'no_quota';
      // If limit is 0, any usage means exceeded
      if (limit === 0) return patientMonths > 0 ? 'exceeded' : 'ok';
      const utilizationPercent = (patientMonths / limit) * 100;
      if (utilizationPercent >= 100) return 'exceeded';
      if (utilizationPercent >= 80) return 'warning';
      return 'ok';
    };

    // Find the clinic that owns the selected topic
    const selectedClinic = getClinicForTopic(selectedReport);

    // Only show quota if selected topic belongs to a clinic with quota
    if (!selectedClinic || !clinicQuotas[selectedClinic.id]) return null;

    const quota = clinicQuotas[selectedClinic.id];
    const quotaStatus = getQuotaStatus(quota.patientMonths, quota.limit);

    // Don't render if no quota set
    if (quotaStatus === 'no_quota') return null;

    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 16px', width: '100%', boxSizing: 'border-box' }}>
        <QuotaBanner
          patientMonths={quota.patientMonths}
          limit={quota.limit}
          quotaStatus={quotaStatus}
        />
      </div>
    );
  };

  const buttonBarStyles = {
    position: 'fixed',
    top: '0px',
    left: '0px',
    right: '0px',
    height: 'auto',
    minHeight: '48px',
    backgroundColor: 'rgb(204, 204, 221)',
    zIndex: 1000,
    transition: 'box-shadow 0.3s ease',
    boxShadow: showShadow ? '0 2px 12px rgba(0,0,0,0.08)' : 'none',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'visible'
  };

  const getButtonStyle = (isActive) => ({
    padding: '8px 16px',
    margin: '0',
    fontSize: '0.9rem',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '20px',
    background: isActive ? '#4285f4' : '#f0f0f0',
    color: isActive ? 'white' : '#333',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  });

  const TopicButton = ({ topicId, topicName }) => (
    <button
      className={`topic-button ${selectedReport === topicId ? 'active' : ''}`}
      style={getButtonStyle(selectedReport === topicId)}
      onClick={() => !isLoading && handleReportSelection(topicId)}
      disabled={isLoading}
    >
      {topicName || topicId}
    </button>
  );

  const ClinicCheckbox = ({ clinic, isSelected, onChange }) => (
    <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '0px 4px' }}>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={(e) => onChange(clinic.id, e.target.checked)}
        style={{ margin: '0px 4px' }}
        disabled={isLoading}
      />
      {clinic.name}
    </label>
  );

  return (
    <div ref={headerRef} className="report-buttons-selection" style={buttonBarStyles}>
      {/* Main toolbar row - topics centered with user icon on right */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: '8px 16px',
        minHeight: '44px',
        boxSizing: 'border-box'
      }}>
        {/* Center section: Topic buttons - takes available space */}
        <div
          className="topic-buttons-scroll"
          style={{
            flex: 1,
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4px 8px',
            overflow: 'visible'
          }}
        >
          {hasAnyTopics && userTopics.map(topic => (
            <TopicButton
              key={topic.id || topic}
              topicId={topic.id || topic}
              topicName={topic.name || topic}
            />
          ))}
        </div>

        {/* Right section: User menu */}
        <div style={{ marginLeft: '12px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <UserInfo
            currentUser={currentUser}
            onSignOut={onSignOut}
            onAdminClick={onAdminClick}
            clinics={availableClinics}
          />
        </div>
      </div>

      {/* Quota pills row (below topic buttons) */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 16px 6px 16px'
      }}>
        <QuotaPills />
      </div>

      {/* Clinic selection row (when available) */}
      {false && showClinicControls && hasAnyClinics && availableClinics.length > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '8px 20px',
          borderTop: '1px solid #f0f0f0',
          backgroundColor: '#fafbfc',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#495057', minWidth: 'fit-content', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <ClinicIcon size={16} />
            Select Clinics:
          </span>
          {availableClinics.map(clinic => (
            <ClinicCheckbox
              key={clinic.id}
              clinic={clinic}
              isSelected={selectedClinics.includes(clinic.id)}
              onChange={onClinicSelectionChange}
            />
          ))}
        </div>
      )}

      {/* Error banner (when present) */}
      {error && (
        <div style={{
          margin: '0 20px 8px 20px',
          color: '#721c24',
          background: '#f8d7da',
          border: '1px solid #f5c6cb',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '0.875rem'
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

// Report Viewer Component - Memoized for performance
const ReportViewer = React.memo(({ selectedReport, showButtonBar, hasAnyTopics, hasAnyClinics, topicContent, loadingTopics, userTopics, visitedTopics, blobUrlsRef, onIframeLoad, headerHeight = 80 }) => {
  const iframeStyles = useMemo(() => ({
    position: 'fixed',
    top: `${headerHeight}px`,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    border: 'none',
    display: 'block'
  }), [headerHeight]);

  const getIframeStyle = useCallback((isVisible) => {
    return {
      ...iframeStyles,
      display: isVisible ? 'block' : 'none'
    };
  }, [iframeStyles]);

  // Filter topics to only render visited ones (lazy loading optimization)
  const topicsToRender = useMemo(() =>
    userTopics.filter(topic => {
      const topicId = topic.id || topic;
      return visitedTopics.has(topicId);
    }),
    [userTopics, visitedTopics]
  );

  return (
    <>
      {/* Static report iframes */}
      <iframe
        id={IFRAME_IDS.EMAGINE}
        title="Care Management Portal"
        style={getIframeStyle(
          selectedReport === REPORT_TYPES.EMAGINE || selectedReport === REPORT_TYPES.DEFAULT
        )}
        frameBorder="0"
        allowFullScreen
      />
      <iframe
        id={IFRAME_IDS.QTHERAPY}
        title="QTherapy"
        style={getIframeStyle(
          selectedReport === REPORT_TYPES.QTHERAPY
        )}
        frameBorder="0"
        allowFullScreen
      />

      {/* Dynamic topic iframes - lazy loaded, only render visited topics */}
      {topicsToRender.map(topic => {
        const topicId = topic.id || topic;
        const topicData = topicContent[topicId];
        const isLoadingTopic = loadingTopics.has(topicId);
        const isVisible = selectedReport === topicId;

        return (
          <div
            key={topicId}
            id={IFRAME_IDS.dynamicTopicContainer(topicId)}
            style={{
              position: 'fixed',
              width: '100%',
              inset: `${headerHeight}px 0px 0px`,
              height: `calc(100% - ${headerHeight}px)`,
              margin: 0,
              padding: 0,
              display: isVisible ? 'block' : 'none',
              backgroundColor: 'white',
              overflow: 'hidden',
              boxSizing: 'border-box'
            }}
          >
            {isLoadingTopic ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                padding: '40px'
              }}>
                <div style={{
                  fontSize: '48px',
                  marginBottom: '20px',
                  animation: 'pulse 2s infinite'
                }}>
                  🔄
                </div>
                <h3 style={{
                  color: '#495057',
                  fontSize: '24px',
                  marginBottom: '12px',
                  fontWeight: '600'
                }}>
                  Loading Topic Content
                </h3>
                <p style={{
                  color: '#6c757d',
                  fontSize: '16px',
                  margin: '0',
                  fontWeight: '400'
                }}>
                  Fetching content...
                </p>
                <div style={{
                  marginTop: '24px',
                  width: '200px',
                  height: '4px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, #4285f4, #34a853, #fbbc05, #ea4335)',
                    animation: 'loading 1.5s infinite'
                  }} />
                </div>
              </div>
            ) : topicData?.error ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)',
                padding: '40px'
              }}>
                <div style={{
                  marginBottom: '20px',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }}>
                  <WarningIcon />
                </div>
                <h3 style={{
                  color: '#dc3545',
                  fontSize: '24px',
                  marginBottom: '12px',
                  fontWeight: '600'
                }}>
                  Failed to Load Content
                </h3>
                <p style={{
                  color: '#6c757d',
                  fontSize: '16px',
                  lineHeight: '1.5',
                  maxWidth: '400px',
                  margin: '0'
                }}>
                  {topicData.error}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    marginTop: '24px',
                    padding: '12px 24px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                >
                  🔄 Try Again
                </button>
              </div>
            ) : topicData?.fileContent ? (
              <iframe
                id={IFRAME_IDS.dynamicTopic(topicId)}
                title={`Dynamic Topic - ${topicId}`}
                style={{ width: '100%', height: '100%', border: 'none' }}
                frameBorder="0"
                allowFullScreen
                ref={(iframe) => {
                  // Use data attribute to track initialization, as iframe.src always has a value (about:blank or parent URL)
                  if (iframe && !iframe.dataset.initialized) {
                    // Reuse existing blob URL or create new one
                    if (!blobUrlsRef.current[topicId]) {
                      const blob = new Blob([topicData.fileContent], { type: 'text/html' });
                      blobUrlsRef.current[topicId] = URL.createObjectURL(blob);
                    }
                    iframe.src = blobUrlsRef.current[topicId];
                    iframe.dataset.initialized = 'true';
                    iframe.onload = () => {
                      const iframeId = IFRAME_IDS.dynamicTopic(topicId);
                      onReportContentLoad(iframe, iframeId);
                      // Notify parent that iframe loaded
                      if (onIframeLoad) {
                        onIframeLoad(iframeId, isVisible);
                      }
                    };
                  }
                }}
              />
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                padding: '40px'
              }}>
                <div style={{
                  fontSize: '48px',
                  marginBottom: '20px',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }}>
                  📄
                </div>
                <h3 style={{
                  color: '#495057',
                  fontSize: '24px',
                  marginBottom: '12px',
                  fontWeight: '600'
                }}>
                  {topicId}
                </h3>
                <p style={{
                  color: '#6c757d',
                  fontSize: '16px',
                  margin: '0',
                  fontWeight: '400'
                }}>
                  Content will appear here once loaded
                </p>
              </div>
            )}
          </div>
        );
      })}

      {/* Welcome screen when no report is selected */}
      {!selectedReport && showButtonBar && (
        <div style={{
          position: 'fixed',
          width: '100vw',
          height: '100vh',
          top: 0,
          left: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          padding: '20px',
          boxSizing: 'border-box',
          zIndex: 10
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '48px 40px',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(255,255,255,0.8)'
          }}>
            <div style={{
              marginBottom: '24px',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
            }}>
              <ChartIcon />
            </div>
            <h2 style={{
              color: '#2c3e50',
              fontSize: '28px',
              fontWeight: '700',
              marginBottom: '16px',
              letterSpacing: '-0.5px'
            }}>
              Welcome to Care Management Portal
            </h2>
            <p style={{
              color: '#5a6c7d',
              fontSize: '18px',
              lineHeight: '1.6',
              margin: '0'
            }}>
              Select a topic from the toolbar above to view your care data and visualizations.
            </p>
          </div>
        </div>
      )}
    </>
  );
});

// Helper functions moved to utils/iframeHelpers.js

// Custom Alert Modal Component (matches ConfirmationModal styling from AdminPanel)
const AlertModal = ({ isOpen, title = 'Notification', message, onClose }) => {
  if (!isOpen) return null;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        animation: 'fadeIn 0.2s ease-in-out'
      }}
      onClick={onClose}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          backgroundColor: '#2b2b2b',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          animation: 'slideIn 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{
          color: '#ffffff',
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '16px',
          marginTop: 0
        }}>
          {title}
        </h2>
        <p style={{
          color: '#e0e0e0',
          fontSize: '16px',
          lineHeight: '1.5',
          marginBottom: '32px',
          marginTop: 0,
          whiteSpace: 'pre-wrap'
        }}>
          {message}
        </p>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            autoFocus
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              fontWeight: '500',
              backgroundColor: '#a8b9ff',
              color: '#1a1a1a',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              minWidth: '100px'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#94a8ff'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#a8b9ff'}
          >
            OK
          </button>
        </div>
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideIn {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// Main App Component
function App() {
  // State management
  const [user, setUser] = useState(undefined);
  const [currentUser, setCurrentUser] = useState(null); // Normalized user data for permissions
  const [currentClinic, setCurrentClinic] = useState(null);
  const [availableClinics, setAvailableClinics] = useState([]);
  const [userTopics, setUserTopics] = useState([]); // Topics accessible to the user
  const [selectedClinics, setSelectedClinics] = useState([]); // Selected clinics for viewing
  const [error, setError] = useState(null);
  const [reportAvailability, setReportAvailability] = useState({
    emagine: false,
    qtherapy: false,
    default: false
  });
  const [selectedReport, setSelectedReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showShadow, setShowShadow] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showPatientRequestForm, setShowPatientRequestForm] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(80); // Dynamic header height
  const [currentClinicQuota, setCurrentClinicQuota] = useState(null);
  const lastFetchKeyRef = useRef(null);
  const selectionCounterRef = useRef(0); // Track report selection order to prevent race conditions
  const selectedReportRef = useRef(selectedReport); // Track current selection for avoiding stale closures

  // Keep selectedReportRef in sync with selectedReport state
  useEffect(() => {
    selectedReportRef.current = selectedReport;
  }, [selectedReport]);

  // Shadow update callback for scroll restoration hook
  const updateShadow = useCallback((scrollY) => {
    setShowShadow(scrollY > SCROLL_THRESHOLD);
  }, []);

  // Use custom hook for iframe scroll restoration
  const { saveScrollPosition, restoreScrollPosition } = useIframeScrollRestoration(updateShadow);

  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: 'Notification',
    message: ''
  });

  // Dynamic topic content state
  const [topicContent, setTopicContent] = useState({});
  const [loadingTopics, setLoadingTopics] = useState(new Set());
  const [visitedTopics, setVisitedTopics] = useState(new Set()); // Track which topics have been visited for lazy loading
  const blobUrlsRef = useRef({}); // Track blob URLs for cleanup

  // Derived state
  const contentAvailable = useMemo(() =>
    Object.values(reportAvailability).some(Boolean) || userTopics.length > 0,
    [reportAvailability, userTopics]
  );

  const showHeader = !contentAvailable;
  const showButtonBar = user !== null; // Always show button bar when authenticated

  // Override window.alert to use custom modal
  useEffect(() => {
    const originalAlert = window.alert;

    window.alert = (message) => {
      setAlertModal({
        isOpen: true,
        title: 'Notification',
        message: String(message || '')
      });
    };

    return () => {
      window.alert = originalAlert;
    };
  }, []);

  // Cleanup blob URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Copy ref value to avoid stale closure - we want the final value at cleanup time
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const blobUrls = blobUrlsRef.current;
      Object.values(blobUrls).forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error revoking blob URL:', error);
        }
      });
    };
  }, []);

  // User authentication and normalization
  const normalizeUserData = useCallback((awsUser) => {
    if (!awsUser) return null;

    // Extract both attributes and JWT claims from AWS Cognito user
    const attributes = {};
    if (awsUser.attributes) {
      Object.entries(awsUser.attributes).forEach(([key, value]) => {
        attributes[key] = value;
      });
    }

    // JWT claims take precedence (from Pre-Token Generation trigger)
    const jwtClaims = awsUser.signInDetails?.authFlowType === 'CUSTOM_AUTH' ?
      {} : (awsUser.idToken?.payload || {});


    // Parse clinic associations from JWT claims (multi-clinic support)
    let clinicAssociations = [];
    if (jwtClaims['custom:all_clinics']) {
      try {
        clinicAssociations = JSON.parse(jwtClaims['custom:all_clinics']);
      } catch (e) {
        console.warn('Failed to parse clinic associations from JWT:', e.message);
      }
    }

    // Current clinic from JWT or fallback to first association
    const primaryClinicId = jwtClaims['custom:clinic_id'] ||
      attributes.family_name ||
      (clinicAssociations.length > 0 ? clinicAssociations[0].clinicId : 'unassigned');

    // Check alternative email sources for Google Auth
    const possibleEmails = [
      attributes.email,
      jwtClaims.email,
      jwtClaims['custom:email'],
      awsUser.signInDetails?.loginId,
      awsUser.username?.includes('@') ? awsUser.username : null
    ].filter(Boolean);

    return {
      username: awsUser.username,
      email: possibleEmails[0] || 'undefined',
      firstName: jwtClaims['custom:first_name'] || attributes.given_name,

      // Add Cognito sub for API operations (v6: userId is the Cognito sub)
      cognitoSub: awsUser.userId || awsUser.attributes?.sub,

      // Multi-tenancy data from JWT claims (efficient)
      clinicId: primaryClinicId,
      clinicAssociations: clinicAssociations, // Array of {clinicId, role, topics} objects
      role: jwtClaims['custom:role'] || attributes.preferred_username || 'viewer',
      topics: jwtClaims['custom:topics'] ?
        jwtClaims['custom:topics'].split(',').filter(t => t.trim()) :
        (attributes['custom:topics'] ?
          attributes['custom:topics'].split(',').filter(t => t.trim()) : []),

      emailVerified: attributes.email_verified === 'true',

      // Convenience boolean flags from JWT claims
      isAdmin: jwtClaims['custom:is_admin'] === 'true',
      isSuperAdmin: jwtClaims['custom:is_super_admin'] === 'true',
      isViewer: jwtClaims['custom:is_viewer'] === 'true',

      // Topic access flags from JWT claims
      hasEmagine: jwtClaims['custom:has_emagine'] === 'true',
      hasQtherapy: jwtClaims['custom:has_qtherapy'] === 'true',
      hasDefault: jwtClaims['custom:has_default'] === 'true'
    };
  }, []);

  // Fetch user's accessible topics based on their assigned topic IDs
  const fetchUserTopics = useCallback(async (user) => {
    if (!user) {
      setUserTopics([]);
      return [];
    }

    try {
      if (user.isSuperAdmin) {
        // Super admins can see all topics
        const allTopics = await topicManager.searchTopics('', 100);
        setUserTopics(allTopics);
        return allTopics;
      } else {
        // Regular users: fetch topic details for their assigned topic IDs
        const userTopicIds = user.topics || [];
        if (userTopicIds.length === 0) {
          setUserTopics([]);
          return [];
        }

        // Get all topics and filter to user's assigned ones
        const allTopics = await topicManager.searchTopics('', 100);
        const userAccessibleTopics = allTopics.filter(topic =>
          userTopicIds.includes(topic.id)
        );

        setUserTopics(userAccessibleTopics);
        return userAccessibleTopics;
      }
    } catch (error) {
      console.error('Error fetching user topics:', error);
      setUserTopics([]);
      return [];
    }
  }, []);

  const handleUser = useCallback(async (userData) => {
    setUser(userData);
    const normalizedUser = normalizeUserData(userData);
    setCurrentUser(normalizedUser);

    // Fetch user's accessible topics
    if (normalizedUser) {
      await fetchUserTopics(normalizedUser);
    }

    // Initialize clinic context for multi-tenancy
    if (normalizedUser && normalizedUser.clinicAssociations) {
      try {
        let clinicDetails;

        // Super admins: load ALL clinics from system for viewing other users
        if (normalizedUser.isSuperAdmin) {
          const allClinicsData = await clinicManager.getAllClinics();
          clinicDetails = Object.entries(allClinicsData.clinics || {}).map(([id, clinic]) => ({
            id,
            name: clinic.name || id,
            role: normalizedUser.clinicAssociations.find(a => a.clinicId === id)?.role || 'super_admin',
            topics: normalizedUser.clinicAssociations.find(a => a.clinicId === id)?.topics || clinic.topics || [],
            patientRequestPlaceholder: clinic.patientRequestPlaceholder || '',
            clinicType: clinic.clinicType || 'MARKET'
          }));
        } else {
          // Regular users: only load their associated clinics
          clinicDetails = await Promise.all(
            normalizedUser.clinicAssociations.map(async (association) => {
              const clinic = await clinicManager.getClinic(association.clinicId);
              return {
                id: association.clinicId,
                name: clinic?.name || association.clinicId,
                role: association.role,
                topics: association.topics,
                patientRequestPlaceholder: clinic?.patientRequestPlaceholder || '',
                clinicType: clinic?.clinicType || 'MARKET'
              };
            })
          );
        }

        setAvailableClinics(clinicDetails);

        // Set current clinic (first one by default)
        const primaryClinic = clinicDetails.find(c => c.id === normalizedUser.clinicId) || clinicDetails[0];
        setCurrentClinic(primaryClinic);

        // Initialize selected clinics to all user clinics by default
        setSelectedClinics(clinicDetails.map(c => c.id));

      } catch (error) {
        console.warn('Failed to initialize clinic context:', error);
        // Fallback: single clinic mode
        const fallbackClinic = {
          id: normalizedUser.clinicId,
          name: normalizedUser.clinicId,
          role: normalizedUser.role,
          topics: normalizedUser.topics,
          clinicType: 'MARKET'  // Default to MARKET for patient request form
        };
        setAvailableClinics([fallbackClinic]);
        setCurrentClinic(fallbackClinic);
      }
    } else {
      // Legacy single-clinic mode
      const singleClinic = {
        id: normalizedUser?.clinicId || 'default',
        name: normalizedUser?.clinicId || 'Default Clinic',
        role: normalizedUser?.role || 'viewer',
        topics: normalizedUser?.topics || ['default'],
        clinicType: 'MARKET'  // Default to MARKET for patient request form
      };
      setAvailableClinics([singleClinic]);
      setCurrentClinic(singleClinic);
      setSelectedClinics([singleClinic.id]);
    }

  }, [normalizeUserData, fetchUserTopics]);

  const getUser = useCallback(() => {
    return getCurrentUser()
      .then(async (basicUser) => {
        // Get complete user data including attributes and JWT tokens
        try {
          const [userAttributes, authSession] = await Promise.all([
            fetchUserAttributes(),
            fetchAuthSession()
          ]);

          // Combine basic user info with attributes and JWT tokens
          const fullUser = {
            ...basicUser,
            attributes: userAttributes,
            idToken: authSession.tokens?.idToken,
            signInDetails: authSession.signInDetails
          };

          handleUser(fullUser);
        } catch (attributeError) {
          console.error('Error fetching user attributes:', attributeError);
          // Fallback to basic user data
          handleUser(basicUser);
        }
      })
      .catch((err) => {
        console.error('Error fetching user:', err);
        setUser(null); // Set to null when no user is signed in
      });
  }, [handleUser]);

  // Function to fetch topic content
  const fetchTopicContent = useCallback(async (topicId) => {
    // Skip if already loading or already fetched
    if (loadingTopics.has(topicId) || topicContent[topicId]) {
      return;
    }

    try {
      setLoadingTopics(prev => new Set(prev).add(topicId));

      const content = await topicManager.getTopicFileContent(topicId);

      setTopicContent(prev => ({
        ...prev,
        [topicId]: content
      }));

    } catch (error) {
      console.error(`Error fetching topic content for ${topicId}:`, error);
      setTopicContent(prev => ({
        ...prev,
        [topicId]: { error: error.message }
      }));
    } finally {
      setLoadingTopics(prev => {
        const newSet = new Set(prev);
        newSet.delete(topicId);
        return newSet;
      });
    }
  }, [loadingTopics, topicContent]);

  // Report selection handler with race condition protection
  const handleReportSelection = useCallback(async (reportType) => {
    console.log('[SELECT] User selected:', reportType);
    // Increment selection counter to track the order of selections
    const selectionId = ++selectionCounterRef.current;
    setSelectedReport(reportType);

    // Check if this is a dynamic topic (not one of the hardcoded REPORT_TYPES)
    const isStaticReport = iframeIdManager.isStaticReport(reportType);
    const iframeId = iframeIdManager.getActiveIframeId(reportType);
    console.log('[SELECT] isStatic:', isStaticReport, 'iframeId:', iframeId);

    if (!isStaticReport) {
      // Check if this topic was already visited
      const alreadyVisited = visitedTopics.has(reportType);
      console.log('[SELECT] Dynamic topic - alreadyVisited:', alreadyVisited);

      // Mark topic as visited for lazy loading
      setVisitedTopics(prev => new Set(prev).add(reportType));

      // This is a dynamic topic, fetch its content
      await fetchTopicContent(reportType);

      // Only restore scroll if this is still the active selection (prevents race condition)
      if (selectionId === selectionCounterRef.current) {
        // Always restore scroll for dynamic topics
        // Use double requestAnimationFrame to ensure iframe is visible and ready
        if (alreadyVisited && topicContent[reportType]) {
          console.log('[SELECT] Restoring scroll for previously visited topic');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (selectionId === selectionCounterRef.current) {
                restoreScrollPosition(iframeId, true);
              }
            });
          });
        } else {
          console.log('[SELECT] First visit - will restore via onload callback');
        }
        // New topics will restore via onload callback
      }
    } else {
      console.log('[SELECT] Static report - restoring scroll');
      // Static report - restore scroll only if still the active selection
      if (selectionId === selectionCounterRef.current) {
        // Use double requestAnimationFrame to ensure iframe is visible
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (selectionId === selectionCounterRef.current) {
              restoreScrollPosition(iframeId, true);
            }
          });
        });
      }
    }
  }, [fetchTopicContent, visitedTopics, topicContent, restoreScrollPosition]);

  // Multi-tenancy handlers
  const handleClinicSwitch = useCallback(async (clinicId) => {
    const targetClinic = availableClinics.find(c => c.id === clinicId);
    if (targetClinic) {
      setCurrentClinic(targetClinic);

      // Update current user context for the new clinic
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          clinicId: targetClinic.id,
          role: targetClinic.role,
          topics: targetClinic.topics
        };
        setCurrentUser(updatedUser);

        // Trigger report refresh for new clinic context
        setIsLoading(true);
        setReportAvailability({ emagine: false, qtherapy: false, default: false });
      }
    }
  }, [availableClinics, currentUser]);

  // Clinic selection handler
  const handleClinicSelectionChange = useCallback((clinicId, isSelected) => {
    setSelectedClinics(prev => {
      if (isSelected) {
        return [...prev, clinicId];
      } else {
        return prev.filter(id => id !== clinicId);
      }
    });
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      // Clear all state
      setUser(null); // Set to null instead of undefined to show SignInSection
      setCurrentUser(null);
      setCurrentClinic(null);
      setAvailableClinics([]);
      setSelectedClinics([]);
      setUserTopics([]);
      setReportAvailability({ emagine: false, qtherapy: false, default: false });
      setError(null);
    } catch (error) {
      console.error('Sign out error:', error);
      setError('Failed to sign out. Please try again.');
    }
  }, []);


  // Admin panel handlers
  const handleAdminClick = useCallback(() => {
    setShowAdminPanel(true);
  }, []);

  const handleAdminClose = useCallback(() => {
    setShowAdminPanel(false);
  }, []);

  // Patient request form handlers
  const handlePatientRequestOpen = useCallback(() => {
    setShowPatientRequestForm(true);
  }, []);

  const handlePatientRequestClose = useCallback(() => {
    setShowPatientRequestForm(false);
  }, []);

  const handlePatientRequestSuccess = useCallback((result) => {
    console.log('Patient request submitted successfully:', result);
    // Optionally show a success message or refresh data
  }, []);

  // Fetch quota status when clinic changes
  useEffect(() => {
    const fetchQuotaStatus = async () => {
      if (!currentClinic?.id || !currentUser) {
        setCurrentClinicQuota(null);
        return;
      }

      try {
        const status = await quotaManager.getClinicQuotaStatus(currentClinic.id);
        setCurrentClinicQuota(status);
      } catch (error) {
        console.error('Error fetching quota status:', error);
        setCurrentClinicQuota(null);
      }
    };

    fetchQuotaStatus();
  }, [currentClinic?.id, currentUser]);

  // Report fetching with clinic context
  useEffect(() => {
    if (!user || !currentClinic) return;

    // Build a stable key for the current fetch context to prevent duplicate loops
    const topicsKey = Array.isArray(currentClinic.topics) ? currentClinic.topics.join(',') : '';
    const fetchKey = `${currentClinic.id}|${topicsKey}`;
    if (lastFetchKeyRef.current === fetchKey) {
      return; // Already fetched for this context; avoid loops
    }
    lastFetchKeyRef.current = fetchKey;

    setIsLoading(true);

    // Include clinic context in the request
    const requestData = {
      clinicId: currentClinic.id,
      topics: currentClinic.topics
    };

    postRequest(requestData)
      .then(res => {

        // Handle both new multi-tenancy format and legacy format
        let emagineReport = null;
        let qtherapyReport = null;
        let defaultReport = null;

        // Check for legacy format first
        if (res.emagineReport && res.qtherapyReport) {
          emagineReport = res.emagineReport;
          qtherapyReport = res.qtherapyReport;
        } else {
          // New multi-tenancy format - extract reports by topic
          Object.entries(res).forEach(([reportName, reportData]) => {
            if (reportData && reportData.content) {
              if (reportData.topic === 'emagine' || reportName.includes('emagine')) {
                emagineReport = reportData.content;
              } else if (reportData.topic === 'qtherapy' || reportName.includes('qtherapy')) {
                qtherapyReport = reportData.content;
              } else if (reportData.topic === 'default' || reportName.includes('default')) {
                defaultReport = reportData.content;
              }
            }
          });

          // Fallback: if no topic-based reports, treat first report as default
          if (!emagineReport && !qtherapyReport && !defaultReport && Object.keys(res).length > 0) {
            const firstReport = Object.values(res)[0];
            defaultReport = firstReport.content || firstReport;
          }
        }


        const newAvailability = {
          emagine: !!emagineReport,
          qtherapy: !!qtherapyReport,
          default: !!defaultReport
        };

        setReportAvailability(newAvailability);

        // Ensure selected report matches availability
        const isSelectedAvailable = (type) => {
          if (type === REPORT_TYPES.EMAGINE) return newAvailability.emagine;
          if (type === REPORT_TYPES.QTHERAPY) return newAvailability.qtherapy;
          if (type === REPORT_TYPES.DEFAULT) return newAvailability.default;
          return false;
        };
        if (!isSelectedAvailable(selectedReport)) {
          const next = newAvailability.emagine
            ? REPORT_TYPES.EMAGINE
            : newAvailability.qtherapy
              ? REPORT_TYPES.QTHERAPY
              : newAvailability.default
                ? REPORT_TYPES.DEFAULT
                : selectedReport;
          setSelectedReport(next);
        }

        // Map reports to their respective iframes
        const reportMap = {
          emagine: { content: emagineReport, id: IFRAME_IDS.EMAGINE },
          qtherapy: { content: qtherapyReport, id: IFRAME_IDS.QTHERAPY },
          default: { content: defaultReport, id: IFRAME_IDS.EMAGINE } // Default uses same iframe as emagine
        };

        // Set each available report in its iframe
        Object.entries(reportMap).forEach(([type, { content, id }]) => {
          if (content) {
            setReportInIframe(content, id, (iframeId) => {
              // Restore scroll position after content loads
              // Use ref to get current selection (avoid stale closure)
              const currentSelection = selectedReportRef.current;
              const isActive = iframeId === iframeIdManager.getActiveIframeId(currentSelection);
              restoreScrollPosition(iframeId, isActive);
            });
          }
        });
      })
      .catch(e => {
        console.error('Error fetching reports:', e);
        setError('Failed to load content. Please try again or contact support.');
      })
      .finally(() => setIsLoading(false));
  }, [user, currentClinic, selectedReport, restoreScrollPosition]);

  // Auth event handling
  useEffect(() => {
    const listener = ({ payload: { event, data } }) => {
      switch (event) {
        case 'signedIn':       // Amplify v6
        case 'signIn':         // Amplify v5 (backward compat)
        case 'signInWithRedirect':  // Amplify v6 OAuth
        case 'cognitoHostedUI':     // Amplify v5 OAuth
          getUser().catch(e => {
            console.error(e);
            setError('Failed to fetch user after sign-in.');
          });
          break;
        case 'signedOut':      // Amplify v6
        case 'signOut':        // Amplify v5
          setUser(null); // Set to null to show SignInSection
          break;
        case 'signIn_failure':
        case 'signInWithRedirect_failure':  // Amplify v6 OAuth failure
        case 'cognitoHostedUI_failure':     // Amplify v5 OAuth failure
          setError('Sign in failed. Please try again.');
          setUser(null);
          break;
        case 'customOAuthState':
          // Handle OAuth redirect with custom state
          console.log('Received OAuth custom state:', data);
          try {
            if (data) {
              const stateData = typeof data === 'string' ? JSON.parse(data) : data;
              if (stateData.action === 'accept-invite' && stateData.inviteToken && stateData.inviteId) {
                // Store state for processing after user is authenticated
                sessionStorage.setItem('oauthInviteToken', stateData.inviteToken);
                sessionStorage.setItem('oauthInviteId', stateData.inviteId);
                console.log('Stored OAuth invitation data for processing');
              }
            }
          } catch (err) {
            console.error('Error parsing OAuth custom state:', err);
          }
          break;
        default:
          break;
      }
    };

    const hubListenerCancellation = Hub.listen('auth', listener);
    getUser();

    return () => {
      if (typeof hubListenerCancellation === 'function') {
        hubListenerCancellation();
      } else if (hubListenerCancellation?.unsubscribe) {
        hubListenerCancellation.unsubscribe();
      }
    };
  }, [getUser]);

  // Handle pending invitation after OAuth sign-in
  useEffect(() => {
    const handlePendingInvitation = async () => {
      // Only process if user is authenticated
      if (!user || !currentUser) return;

      // Check for OAuth invitation first (from customState), then fall back to legacy sessionStorage
      let inviteToken = sessionStorage.getItem('oauthInviteToken');
      let inviteId = sessionStorage.getItem('oauthInviteId');
      let isOAuthInvite = !!(inviteToken && inviteId);

      // Fall back to legacy sessionStorage if OAuth state wasn't available
      if (!inviteToken) {
        inviteToken = sessionStorage.getItem('pendingInviteToken');
        inviteId = sessionStorage.getItem('pendingInviteId');
      }

      if (inviteToken && inviteId) {
        try {
          console.log('Processing invitation after OAuth sign-in:', inviteId);

          // Clear the stored invitation data first to prevent reprocessing
          sessionStorage.removeItem('oauthInviteToken');
          sessionStorage.removeItem('oauthInviteId');
          sessionStorage.removeItem('pendingInviteToken');
          sessionStorage.removeItem('pendingInviteId');

          // Call the acceptInvite API to process the invitation
          // This is necessary because PostConfirmation Lambda cannot access OAuth customState
          const session = await fetchAuthSession();
          const accessToken = session?.tokens?.accessToken?.toString?.() || session?.tokens?.accessToken;

          const response = await fetch(`${getApiEndpoint()}/api`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
              operation: 'acceptInvite',
              inviteId: inviteId,
              token: inviteToken,
              cognitoSub: user.userId
            })
          });

          const result = await response.json();

          if (!response.ok || !result.success) {
            throw new Error(result.error || 'Failed to process invitation');
          }

          console.log('Successfully processed OAuth invitation:', result);

          // Redirect to home page to show the authenticated content with new clinic access
          window.location.href = '/';
        } catch (error) {
          console.error('Error processing OAuth invitation:', error);
          // Show error to user since invitation processing failed
          setError(`Failed to process invitation: ${error.message}`);
        }
      }
    };

    handlePendingInvitation();
  }, [user, currentUser]);

  // Set up scroll listener for the shadow effect
  useEffect(() => {
    if (!contentAvailable || !showButtonBar) return;

    const isStaticReport = iframeIdManager.isStaticReport(selectedReport);

    // Handle scroll for static iframe
    const handleIframeScroll = () => {
      const activeIframeId = iframeIdManager.getActiveIframeId(selectedReport);
      const iframe = document.getElementById(activeIframeId);

      if (iframe?.contentWindow) {
        try {
          const scrollY = iframe.contentWindow.scrollY ||
            iframe.contentDocument?.documentElement?.scrollTop || 0;
          saveScrollPosition(activeIframeId, scrollY);
          updateShadow(scrollY);
        } catch (error) {
          console.error("Couldn't access iframe scroll position:", error);
        }
      }
    };

    // Handle postMessage from iframes
    const handleIframeMessage = (event) => {
      if (event.data?.type !== 'scroll-position') return;

      // Get iframe ID from message data (injected in scroll reporter script)
      const sourceIframeId = event.data.iframeId;
      console.log('[MESSAGE] Received scroll - sourceId:', sourceIframeId, 'scrollY:', event.data.scrollY);

      if (!sourceIframeId) {
        console.log('[MESSAGE] No iframeId in message!');
        return;
      }

      // Only process scroll events from the currently active iframe
      // This prevents hidden iframes from overwriting saved positions with scrollY: 0
      const activeId = iframeIdManager.getActiveIframeId(selectedReport);
      if (sourceIframeId === activeId) {
        console.log('[MESSAGE] Active iframe - saving position and updating shadow');
        saveScrollPosition(sourceIframeId, event.data.scrollY);
        updateShadow(event.data.scrollY);
      } else {
        console.log('[MESSAGE] Inactive iframe - ignoring scroll event to prevent overwrite');
      }
    };

    // Setup and initialization
    // Note: Scroll restoration now happens in iframe onload callbacks for reliability
    // This ensures scroll position is restored exactly when content is ready
    if (isStaticReport) {
      // Static iframe: check current scroll position
      handleIframeScroll();
    }

    // Setup message listener
    window.addEventListener('message', handleIframeMessage);

    // Cleanup
    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [contentAvailable, showButtonBar, selectedReport, saveScrollPosition, updateShadow]);

  // Auto-select first available content (prioritize topics over static reports)
  useEffect(() => {
    // Only auto-select if nothing is currently selected
    if (!selectedReport) {
      // Prioritize user topics
      if (userTopics.length > 0) {
        const firstTopic = userTopics[0];
        const topicId = firstTopic.id || firstTopic;
        handleReportSelection(topicId);
      }
      // Fallback to static reports if no topics
      else if (Object.values(reportAvailability).some(Boolean)) {
        const firstAvailable = reportAvailability.emagine
          ? REPORT_TYPES.EMAGINE
          : reportAvailability.qtherapy
            ? REPORT_TYPES.QTHERAPY
            : reportAvailability.default
              ? REPORT_TYPES.DEFAULT
              : null;
        if (firstAvailable) {
          setSelectedReport(firstAvailable);
        }
      }
    }
  }, [userTopics, selectedReport, reportAvailability, handleReportSelection]);

  return (
    <div style={styles.container}>
      {/* Header Section */}
      {showHeader && (
        <>
          <div style={{ paddingTop: showButtonBar ? '140px' : '60px' }}>
            <Header />
          </div>
          {error && <div className="error-message">{error}</div>}
        </>
      )}

      {/* Main Content */}
      {user === undefined ? (
        // Loading state while checking authentication
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          fontSize: '16px',
          color: '#6c757d'
        }}>
          Loading...
        </div>
      ) : user ? (
        <>
          {isLoading && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px',
              color: '#6c757d', paddingTop: '80px'
            }}>
              Loading...
            </div>
          )}
          {!isLoading && !contentAvailable && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start', // Align to top instead of center
              minHeight: 'calc(100vh - 300px)',
              paddingTop: '80px', // Fixed distance from header
              paddingBottom: '40px',
              paddingLeft: '20px',
              paddingRight: '20px',
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '48px 40px',
                maxWidth: '600px',
                width: '100%',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                border: '1px solid rgba(255,255,255,0.8)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Subtle background decoration */}
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  right: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'radial-gradient(circle, rgba(66,133,244,0.03) 0%, transparent 70%)',
                  pointerEvents: 'none'
                }} />

                <div style={{
                  position: 'relative',
                  zIndex: 1
                }}>
                  <div style={{
                    marginBottom: '24px',
                    opacity: 0.8,
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                  }}>
                    <ChartIcon />
                  </div>

                  <h2 style={{
                    color: '#2c3e50',
                    fontSize: '28px',
                    fontWeight: '700',
                    marginBottom: '16px',
                    margin: '0 0 16px 0',
                    letterSpacing: '-0.5px'
                  }}>
                    No Content Available
                  </h2>

                  <p style={{
                    color: '#5a6c7d',
                    fontSize: '18px',
                    lineHeight: '1.6',
                    marginBottom: '32px',
                    margin: '0 0 32px 0',
                    fontWeight: '400'
                  }}>
                    There is currently no content available for your clinic and assigned topics.
                  </p>

                  <div style={{
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '20px',
                    borderLeft: '4px solid #4285f4'
                  }}>
                    <p style={{
                      color: '#495057',
                      fontSize: '15px',
                      lineHeight: '1.5',
                      margin: '0',
                      fontWeight: '500'
                    }}>
                      {permissionManager.canManageInvites(currentUser) ? (
                        <>
                          <strong>👨‍💼 Administrator:</strong> Use the Admin panel above to manage users and invite new members to your clinic.
                        </>
                      ) : (
                        <>
                          <strong>📞 Need Access?</strong> Contact your clinic administrator for access to additional topics.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <ReportViewer
            selectedReport={selectedReport}
            showButtonBar={showButtonBar}
            hasAnyTopics={userTopics.length > 0}
            hasAnyClinics={availableClinics.length > 0}
            topicContent={topicContent}
            loadingTopics={loadingTopics}
            userTopics={userTopics}
            visitedTopics={visitedTopics}
            blobUrlsRef={blobUrlsRef}
            headerHeight={headerHeight}
            onIframeLoad={(iframeId, isVisible) => {
              // Restore scroll position after iframe content loads
              restoreScrollPosition(iframeId, isVisible);
            }}
          />

          {/* Report Navigation */}
          {showButtonBar && (
            <ButtonBar
              selectedReport={selectedReport}
              handleReportSelection={handleReportSelection}
              reportAvailability={reportAvailability}
              showShadow={showShadow}
              currentUser={currentUser}
              onAdminClick={handleAdminClick}
              onClinicSwitch={handleClinicSwitch}
              onSignOut={handleSignOut}
              isLoading={isLoading}
              error={error}
              userTopics={userTopics}
              availableClinics={availableClinics}
              selectedClinics={selectedClinics}
              onClinicSelectionChange={handleClinicSelectionChange}
              onHeightChange={setHeaderHeight}
            />
          )}

          {/* Admin Panel with multi-tenancy context */}
          <AdminPanel
            isOpen={showAdminPanel}
            onClose={handleAdminClose}
            currentUser={currentUser}
            currentClinic={currentClinic}
            availableClinics={availableClinics}
          />

          {/* Patient Request Form Modal */}
          <PatientRequestForm
            isOpen={showPatientRequestForm}
            onClose={handlePatientRequestClose}
            clinicId={currentClinic?.id}
            clinicName={currentClinic?.name}
            clinicType={currentClinic?.clinicType}
            clinicPlaceholder={currentClinic?.patientRequestPlaceholder}
            onSuccess={handlePatientRequestSuccess}
          />

          {/* Floating Action Button - Request New Patient (only shows when clinic has quota) */}
          {currentClinicQuota && currentClinicQuota.quotaStatus !== 'no_quota' && !showAdminPanel && (
            <button
              onClick={handlePatientRequestOpen}
              style={{
                position: 'fixed',
                bottom: '24px',
                right: '24px',
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                backgroundColor: '#4285f4',
                color: 'white',
                border: 'none',
                boxShadow: '0 4px 12px rgba(66, 133, 244, 0.4)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: 'bold',
                transition: 'all 0.2s ease',
                zIndex: 1000
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#3367d6';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#4285f4';
                e.target.style.transform = 'scale(1)';
              }}
              title="Request New Patient"
            >
              +
            </button>
          )}

          {/* Custom Alert Modal */}
          <AlertModal
            isOpen={alertModal.isOpen}
            title={alertModal.title}
            message={alertModal.message}
            onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
          />
        </>
      ) : (
        <SignInSection onSignInSuccess={getUser} />
      )}
    </div>
  );
}

// Main Application Component 
const MainApp = () => {
  return (
    <Router>
      <Routes>
        <Route path="/accept-invite" element={<InvitationAccept />} />
        <Route path="/" element={<App />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default MainApp;
